import { ChatBot, NextPageHead, PageView } from '@aspendental/shared-components-web';
import { convertNameToUrlFormat } from '@aspendental/shared-components-web/lib/utils/convertNameToUrlFormat/convertNameToUrlFormat';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getBeffeGraphQlUrl, getRootLevelConfig, getTagTheme } from '../utils';
import ProviderDetailsPage from '@src/components/ProviderDetailsPage/ProviderDetailsPage';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, ProviderDetailsByIdDocument, ProviderDetailsPageByIdDocument, ProviderPicByNpiDocument, type Page } from '@src/lib/__generated/sdk';
import { getBeffeGraphQlClient, getEnterpriseAPIGraphQlClient } from '@src/lib/client';
import { getLogger } from '@src/lib/logging/logger';
import choosePreview from '@src/pages/utils/choose-preview';
import getConfigServices from '@src/pages/utils/get-config-services';
import { getDeviceTypeFromHeaders } from '@src/pages/utils/get-device-type-from-headers';
import { getServerSideTranslations } from '@src/pages/utils/get-serverside-translations';
import { getUserCoordinates } from '@src/pages/utils/get-user-coordinates';
import { cleanProviderPicByNpi } from '@src/util/CleanProviderPicByNpi';
import { interpolateContent } from '@src/util/contentVariableInsertion';
import { generateProviderSchemaObject } from '@src/util/providerJsonSchema';
const Page = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const {
    config,
    showProviderPageInsuranceInfo,
    googleMapsData
  } = props;
  const {
    siteURL,
    features,
    featureFlags
  } = config as BrandThemeConfig;
  const {
    url: logoUrl
  } = props.appData?.logo ?? {};
  const {
    doctorDetails,
    path,
    breadcrumbsData,
    providerPageContent,
    pageReferrer
  } = props;
  const {
    firstName,
    lastName,
    mainWorkerFunction
  } = doctorDetails.provider;

  // page title and desc
  // can be updated based on seo recs later
  const pageTitle = `${firstName} ${lastName} | ${mainWorkerFunction} | ${config.name}`;
  const pageDesc = `Come meet ${firstName} ${lastName}, an expert ${mainWorkerFunction}. At ${config.name}, we offer quality & affordable dental care. Schedule your free consultation now.`;
  return <>
			<PageView siteSection="Office Details" pageReferrer={pageReferrer} data-sentry-element="PageView" data-sentry-source-file="[...slug].page.tsx" />
			<NextPageHead path={path} title={pageTitle} indexed={true} description={pageDesc} canonical={`${siteURL}${features.providers.root}${convertNameToUrlFormat(`${firstName} ${lastName}`)}${path}`} originUrl={siteURL} breadcrumbTitle={breadcrumbsData.breadcrumbTitle} breadcrumbs={breadcrumbsData.breadcrumbsCollection.items} ogImage={providerPageContent?.ogImage?.desktopImage?.image?.cdn_url || doctorDetails.providerPicByNpi?.[0]?.url || ''} data-sentry-element="NextPageHead" data-sentry-source-file="[...slug].page.tsx" />
			{features?.providerSchema && <script id="json-ld-provider-schema" type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(generateProviderSchemaObject(config, doctorDetails, path))
    }} />}
			<ProviderDetailsPage provider={{
      ...doctorDetails.provider,
      providerImages: doctorDetails.providerPicByNpi
    }} config={config} contentsCollection={providerPageContent?.contentsCollection} secondaryContentsCollection={providerPageContent?.secondaryContentsCollection} showProviderPageInsuranceInfo={showProviderPageInsuranceInfo} googleMapsData={googleMapsData} data-sentry-element="ProviderDetailsPage" data-sentry-source-file="[...slug].page.tsx" />
			{features.chat.enabled && <ChatBot url={features.chat.chatBotURL} isIconButton={features.chat.isIconButton} logoUrl={logoUrl} featureList={featureFlags.featureList} />}
		</>;
};
export const getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
  req,
  preview = false
}) => {
  if (!params?.slug) {
    return NOT_FOUND;
  }
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const loggerName = 'provider-detail-page';
  const tagTheme = getTagTheme(req);
  const logger = getLogger('provider-detail-page', tagTheme);
  const slugs = params.slug as string[];
  // this path value needed for page indexing
  const path = '/' + slugs.join('/');
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    preview = choosePreview(req.headers.host, preview);
    const {
      brandConfig,
      gqlClient,
      appName
    } = getRootLevelConfig({
      req,
      preview,
      loggerName
    });
    logger.info(path);
    const [appPagesData, appMetadata, translations] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    const appData = {
      ...appPagesData.data?.appCollection?.items[0],
      ...appMetadata.data?.appCollection?.items[0]
    };

    // replace [env] with environment name for locationBarFindOfficeUrl
    // TODO: create a brand variable context to replace those variables from CMS in future
    if (appData && appData.header?.locationBarFindOfficeUrl) {
      const brandSubdomain = process.env.ENVIRONMENT_NAME !== 'prod' ? 'wwwstg' : 'www';
      appData.header.locationBarFindOfficeUrl = appData.header?.locationBarFindOfficeUrl?.replace('[env]', brandSubdomain);
    }
    if (!appData) {
      logger.error('PP_APP_ERROR');
      return NOT_FOUND;
    }

    //only the last section in the url path is the slug to query
    const pathForQuery = slugs[slugs.length - 1];
    const enterpriseAPIGraphQlClient = getEnterpriseAPIGraphQlClient();
    const updatedServices = getConfigServices(req, brandConfig);
    const beffeGraphQlUrl = getBeffeGraphQlUrl(updatedServices);
    logger.info(`PP_BEFFE_URL: ${beffeGraphQlUrl}`);
    const beffeGraphQlClient = getBeffeGraphQlClient(beffeGraphQlUrl);
    const [providerData, providerPicturesData] = await Promise.all([enterpriseAPIGraphQlClient.query(ProviderDetailsByIdDocument, {
      npi: pathForQuery
    }).toPromise(), beffeGraphQlClient.query(ProviderPicByNpiDocument, {
      npi: pathForQuery,
      brand: brandConfig.bffBrandType
    }).toPromise()]);
    if (providerData.error) {
      logger.error(`COULD_NOT_FIND_THE_PROVIDER_SLUG: ${pathForQuery} (${providerData.error})`);
      return NOT_FOUND;
    }
    if (!providerData.data.provider) {
      logger.error(`INVALID_PROVIDER_NUMBER: ${pathForQuery}`);
      return NOT_FOUND;
    }
    if (providerPicturesData.error) {
      logger.error(`COULD_NOT_FIND_THE_PROVIDER_PICTURES_SLUG: ${pathForQuery} (${providerPicturesData.error})`);
      providerPicturesData.data = {
        providerPicByNpi: []
      };
    }
    const doctorDetails = {
      ...providerData.data,
      ...providerPicturesData.data
    };
    doctorDetails.providerPicByNpi = cleanProviderPicByNpi(doctorDetails?.providerPicByNpi);

    // breadcrumb data generation for provider page
    const breadcrumbsData = {
      breadcrumbTitle: `${doctorDetails.provider.firstName} ${doctorDetails.provider.lastName}`,
      path: path,
      breadcrumbsCollection: {
        items: [{
          breadcrumbTitle: 'Home',
          path: '/'
        } as Page, {
          breadcrumbTitle: 'Providers',
          path: brandConfig.features.providers.root
        }]
      }
    };

    // contentful contents for provider page
    let pageContentData = await gqlClient.query(ProviderDetailsPageByIdDocument, {
      brandName: brandConfig.name,
      npiId: pathForQuery,
      preview
    }).toPromise();
    const providerDetailsPageCollectionItems = pageContentData?.data?.providerDetailsPageCollection?.items || [];
    if (providerDetailsPageCollectionItems.length !== 1) {
      pageContentData = await gqlClient.query(ProviderDetailsPageByIdDocument, {
        brandName: brandConfig.name,
        npiId: 'Default',
        preview
      }).toPromise();
    }
    const providerPageContent = pageContentData?.data?.providerDetailsPageCollection?.items[0];
    const pageStructure = {
      title: '',
      description: '',
      canonical: '',
      ...providerPageContent
    };
    const mainFacilityLocation = doctorDetails.provider.providerLocations?.find(({
      isMain
    }) => isMain);
    const mainFacilityName = mainFacilityLocation?.facility?.name || '';
    const mainFacilityCity = mainFacilityLocation?.facility?.location?.city || '';
    const mainFacilityState = mainFacilityLocation?.facility?.location?.stateCode || '';
    const interpolatedPageData = interpolateContent(pageStructure as any, [{
      value: mainFacilityName,
      regex: /\[name\]/g
    }, {
      value: mainFacilityCity,
      regex: /\[city\]/g
    }, {
      value: mainFacilityState,
      regex: /\[state\]/g
    }]);
    return {
      props: {
        ...translations,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: {
          ...brandConfig,
          ...(appData.featureFlags ? {
            featureFlags: appData.featureFlags
          } : {}),
          services: updatedServices,
          schemaUrl: `${brandConfig.siteURL}${req.url}`
        },
        doctorDetails: doctorDetails,
        ...(interpolatedPageData ? {
          providerPageContent: interpolatedPageData
        } : {}),
        appData,
        user: getUserCoordinates(req, logger),
        footerData: appData?.footer,
        path,
        breadcrumbsData,
        preview,
        deviceType,
        showProviderPageInsuranceInfo: appData?.featureFlags?.showProviderPageInsuranceInfo,
        googleMapsData: appData?.header?.googleMapsForLocationCards,
        pageReferrer,
        stateCodeFromHeaders
      }
    };
  } catch (e) {
    logger.error(`PROVIDER_DETAIL_PAGE_LOAD_FAILURE: PATH - ${path} ERROR - ${e}`);
    return NOT_FOUND;
  }
};
export default Page;